import { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import type { AttentionLevel, RecurrenceRule } from '@/src/renderer/lib/db/models'
import type { ParsedTitle, TitleToken, TitleTokenKind } from '@/src/renderer/lib/titleParser'
import type { StartValue } from '../../WhenDropdown'
import type { DeadlineValue } from '../../DeadlineDropdown'
import type { DurationValue } from '../TaskEntry.types'

export type RecognizedField = 'start' | 'deadline' | 'duration' | 'attention' | 'recurrence'

interface FieldValues {
  start: StartValue | null
  deadline: DeadlineValue | null
  duration: DurationValue | null
  attention: AttentionLevel | null
  recurrence: RecurrenceRule | null
  tags: string[]
}

interface FieldSetters {
  setStart: (value: StartValue | null) => void
  setDeadline: (value: DeadlineValue | null) => void
  setDuration: (value: DurationValue | null) => void
  setAttention: (value: AttentionLevel | null) => void
  setRecurrence: (value: RecurrenceRule | null) => void
  setTags: (value: string[]) => void
}

interface UseRecognizedFieldsArgs {
  parsed: ParsedTitle
  values: FieldValues
  setters: FieldSetters
  dismiss: (tokens: TitleToken[]) => void
  /** Wechselt, wenn das Formular geleert wird — dann gibt es nichts zurückzustellen. */
  resetSignal: unknown
}

/** Welche Arten ein Feld setzen: Die Regel gibt auch den Tag, „9 bis 10 Uhr“ auch die Dauer. */
const SOURCES: Record<RecognizedField, TitleTokenKind[]> = {
  start: ['when', 'recurrence'],
  deadline: ['deadline'],
  duration: ['duration', 'when'],
  attention: ['attention'],
  recurrence: ['recurrence'],
}

/** Was eine Änderung von Hand als Text lässt — nur die eigene Art, nicht „jeden Montag“ für ein neues Wann. */
const OWN: Record<RecognizedField, TitleTokenKind> = {
  start: 'when', deadline: 'deadline', duration: 'duration', attention: 'attention', recurrence: 'recurrence',
}

const FIELDS = Object.keys(SOURCES) as RecognizedField[]

const tokenKey = (token: TitleToken) => `${token.kind}:${token.text.toLowerCase()}`

function recognizedValue(field: RecognizedField, fields: ParsedTitle['fields']): FieldValues[RecognizedField] | undefined {
  switch (field) {
    case 'start': return fields.start && { date: fields.start.date, time: fields.start.time, period: fields.start.period }
    case 'deadline': return fields.deadline && { date: fields.deadline.date, time: fields.deadline.time, period: fields.deadline.period }
    case 'duration': return fields.duration && { minutes: fields.duration.minutes, mode: fields.duration.mode }
    case 'attention': return fields.attention
    case 'recurrence': return fields.recurrence
  }
}

/**
 * **Erkanntes setzt die Chips der Zeile** (#234, Entscheidung 12: die letzte Handlung gewinnt).
 *
 * - Ein erkanntes Wort setzt sein Feld still (ohne Klang) und merkt sich den Wert davor.
 * - Verschwindet das Wort — gelöscht oder als Text gelassen —, kehrt der Wert davor zurück.
 * - Ändert man den Chip von Hand, wird das Wort Text, und der Wert von Hand bleibt. Erst ein neues
 *   Wort derselben Art darf das Feld wieder setzen.
 * - Tags kommen dazu statt zu ersetzen; nimmt man einen erkannten von Hand weg, wird sein Wort Text.
 *
 * Als Layout-Effekt: Die Chips stehen im selben Bild wie die Markierung.
 */
export function useRecognizedFields({ parsed, values, setters, dismiss, resetSignal }: UseRecognizedFieldsArgs) {
  const applied = useRef(new Map<RecognizedField, { key: string; before: unknown }>())
  const manual = useRef(new Map<RecognizedField, Set<string>>())
  const addedTags = useRef(new Set<string>())
  const valuesRef = useRef(values)
  valuesRef.current = values
  const settersRef = useRef(setters)
  settersRef.current = setters
  const parsedRef = useRef(parsed)
  parsedRef.current = parsed

  const resetRef = useRef(resetSignal)
  if (resetRef.current !== resetSignal) {
    resetRef.current = resetSignal
    applied.current.clear()
    manual.current.clear()
    addedTags.current.clear()
  }

  useLayoutEffect(() => {
    const { fields, tokens } = parsed
    const set = settersRef.current
    const current = valuesRef.current
    const write: Record<RecognizedField, (value: never) => void> = {
      start: set.setStart, deadline: set.setDeadline, duration: set.setDuration,
      attention: set.setAttention, recurrence: set.setRecurrence,
    }

    for (const field of FIELDS) {
      const keys = tokens.filter(t => SOURCES[field].includes(t.kind)).map(tokenKey).sort()
      const value = recognizedValue(field, fields)
      const key = keys.join('|')

      const frozen = manual.current.get(field)
      if (frozen) {
        if (!keys.some(k => !frozen.has(k))) continue
        manual.current.delete(field)
      }

      const prev = applied.current.get(field)
      if (value != null) {
        if (prev?.key === key) continue
        applied.current.set(field, { key, before: prev ? prev.before : current[field] })
        write[field](value as never)
      } else if (prev) {
        applied.current.delete(field)
        write[field](prev.before as never)
      }
    }

    const known = fields.tags.flatMap(tag => (tag.id ? [tag.id] : []))
    const toAdd = known.filter(id => !current.tags.includes(id))
    const toRemove = [...addedTags.current].filter(id => !known.includes(id))
    if (toAdd.length || toRemove.length) {
      toAdd.forEach(id => addedTags.current.add(id))
      toRemove.forEach(id => addedTags.current.delete(id))
      set.setTags([...current.tags.filter(id => !toRemove.includes(id)), ...toAdd])
    }
  }, [parsed])

  /** Den Wert des Feldes gibt jetzt die Hand vor — kein Zurückstellen, kein erneutes Setzen durch dasselbe Wort. */
  const freeze = useCallback((field: RecognizedField) => {
    const { tokens } = parsedRef.current
    applied.current.delete(field)
    manual.current.set(field, new Set(tokens.filter(t => SOURCES[field].includes(t.kind)).map(tokenKey)))
  }, [])

  /** Ein Chip wurde von Hand geändert: sein Wort wird Text. */
  const onManualChange = useCallback((field: RecognizedField) => {
    const own = parsedRef.current.tokens.filter(t => t.kind === OWN[field])
    freeze(field)
    // Die Regel von Hand setzt auch das Wann (`recurrenceUpdate`) — das darf nicht zurückspringen.
    if (field === 'recurrence') freeze('start')
    if (own.length) dismiss(own)
  }, [dismiss, freeze])

  /** Die Tags von Hand: Wer einen erkannten wegnimmt, lässt sein Wort als Text. */
  const onManualTags = useCallback((next: string[]) => {
    const removed = [...addedTags.current].filter(id => !next.includes(id))
    if (!removed.length) return
    removed.forEach(id => addedTags.current.delete(id))
    const { fields, tokens } = parsedRef.current
    const names = fields.tags.filter(tag => tag.id && removed.includes(tag.id)).map(tag => tag.name.toLowerCase())
    const words = tokens.filter(t => t.kind === 'tag' && names.includes(t.text.slice(1).toLowerCase()))
    if (words.length) dismiss(words)
  }, [dismiss])

  return useMemo(() => ({ onManualChange, onManualTags }), [onManualChange, onManualTags])
}
